import { EAuthStatus, ETaxInvoiceStatus, IPageParam, EHnInvoiceApplyPay, ETaxInvoiceApplyStatus, ETaxSourceRegister, IServerOptionsItem, IEnterpriseItem, EHnInvoiceType } from '@core/main/types';

export interface IInvoiceApplicationParams {
  /** 企业ID */
  companyId?: number;
  /** 服务商ID */
  serverId?: number;
}

/**
 * 检查众包订单所属自由职业者状态请求参数
 */
export interface IOrdersCheckParams {
  /** 页码 */
  page?: number;
  /** 每页数量 */
  size?: number;
  /** 企业ID */
  companyId?: number;
  /** 发票科目ID */
  invoiceSubjectId?: number;
  /** 自由职业者 */
  freelancer?: string;
  /** 实名状态（海南）（0:未实名 1：已实名） */
  hnAuthStatus?: EAuthStatus;
  /** 绑定企业状态（0:未绑定 1：已绑定） */
  companyBindStatus?: ECompanyBindStatus;
  /** 合同签署状态 */
  contractStatus?: ETaxContractStatus;
  /** 税务登记 */
  hnTaxStatus?: ETaxSourceRegister;
}

/**
 * 自由职业者警告信息
 */
export interface IWarningFreelancer {
  /** 开票科目ID */
  invoiceSubjectId: number;
  /** 开票科目名称 */
  invoiceSubjectName: string;
  /** 自由职业者 */
  freelancer: string;
  /** 自由职业者身份证号 */
  freelancerIdCard: string;
  /** 自由职业者手机号 */
  freelancerPhone: string;
  /** 异常订单数 */
  warningOrderCount: number;
  /** 异常订单编号 */
  warningOrders: string;
  /** 实名状态（海南）（0:未实名 1：已实名） */
  hnAuthStatus: EAuthStatus;
  /** 绑定企业状态（0:未实名 1：已实名） */
  companyBindStatus: ECompanyBindStatus;
  /** 合同签署状态 */
  contractStatus: ETaxContractStatus;
}

/**
 * 完税开票科目列表
 */
export interface IInvoiceSubjectAndTaxItem {
  /** 科目ID */
  invoiceSubId: number;
  /** 科目名 */
  invoiceSubName: string;
  /** 含税总金额 */
  taxAmount: number;
  /** 含税总金额(不含异常订单) */
  taxAmountValid: number;
  /** 业务主体ID（企业/服务商） */
  businessId: number;
  /** 订单检查结果 */
  orderCheckResult: IInvoiceSubjectOrderResult;
  /** 小额免税订单数 */
  freeTaxOrderCount: number;
  /** 小额免税订单金额 */
  freeTaxOrderAmount: number;
  /** 标准订单金额 */
  standardOrderAmount: number;
}

/**
 * 订单检查结果
 */
export interface IInvoiceSubjectOrderResult {
  /** 异常订单数 */
  warningOrderCount: number;
  /** 异常订单含税金额 */
  warningOrderAmount: number;
}

/**
 * 开票科目子订单分页入参
 */
export interface IInvoiceDetailParams extends IOrdersCheckParams {
  /** 科目ID */
  invoiceSubId?: number;
  /** 服务商Id */
  serverId?: number;
}

/**
 * 开票科目子订单分页
 */
export interface IInvoiceDetailItem {
  /** 子订单编号 */
  orderDetailNo: string;
  /** 任务ID */
  dutyId: number;
  /** 任务名 */
  dutyName: string;
  /** 含税金额 */
  taxAmount: number;
  /** 订单创建时间 */
  orderStartTime: string;
  /** 订单完成时间 */
  orderEndTime: string;
  /** 收款人姓名 */
  personName: string;
  /** 收款人身份证号 */
  personIdNumber: string;
  /** 收款人手机号 */
  personTel: string;
  /** 收款人银行卡号 */
  personBank: string;
  /** 收款人到账金额 */
  receivedAmount: string;
}

/**
 * 开票申请入参
 */
export interface ISubmitInvoiceParams {
  /** 发票科目ID */
  invoiceSubjectId?: number;
  /** 企业Id */
  companyId?: number;
  /** 服务商Id */
  serverId?: number;
  /** 发票申请类型 */
  invoiceType?: ETaxInvoiceApplyStatus;
  /** 订单ID列表 */
  orderDetailIds?: number[];
}

export enum ECompanyBindStatus {
  /** 已绑定 */
  Yes = 1,
  /** 未绑定 */
  No = 0,
}

export const companyBindStatusLabel = {
  [ECompanyBindStatus.Yes]: '已绑定',
  [ECompanyBindStatus.No]: '未绑定',
};

export const companyBindStatusStyle = {
  [ECompanyBindStatus.Yes]: 'success',
  [ECompanyBindStatus.No]: 'info',
};

export enum ETaxContractStatus {
  /** 未完全签署 */
  No = 0,
  /** 已签署 */
  Yes = 1,
}

export const taxContractStatusLabel = {
  [ETaxContractStatus.Yes]: '已签署',
  [ETaxContractStatus.No]: '未全部签署',
};

/**
 * 异常订单列表
 */
export interface IWarningOrdersList {
  /** 开票科目ID */
  invoiceSubjectId: number;
  /** 开票科目名称 */
  invoiceSubjectName: string;
}

/**
 * 完税发票详情入参
 */
export interface ITaxInvoiceDetailParams extends IPageParam {
  /** 关联的申请单ID */
  applicationId?: number;
  /** 业务主体类型\n SERVER：服务商\n COMPANY：企业\n */
  businessType?: 'SERVER' | 'COMPANY';
}

/**
 * 发票申请单关联订单响应数据
 */
export interface IHnInvoiceApplicationOrders {
  /** 数据列表 */
  data: IHnInvoiceApplicationOrderItem[];
  /** 总数 */
  total: number;
  /** 开票科目 */
  invoiceSubjectId: number;
  /** 开票科目名称 */
  invoiceSubjectName: string;
  /** 到账总金额 */
  receivedAmountTotal: number;
  /** 月开票金额 */
  invoiceMonth: number;
  /** 剩余开票金额 */
  invoiceAmountRemaining: number;
  /** 发票已用额度 */
  invoiceUsedQuota: number;
  /** 发票金额 */
  invoiceAmount: number;
  /**
   * 完税状态
   * PENDING_PAYMENT：待支付
   * PENDING_INVOICE：待开票
   * INVOICED：已开票
   */
  taxStatus: ETaxInvoiceStatus;
  /** 异常订单数 */
  warningOrderCount: number;
  /** 发票申请类型 */
  invoiceType: ETaxInvoiceApplyStatus;
  /** 海南开票方式 */
  invoiceApplyChannel: EHnInvoiceType;
}

/**
 * 发票申请单关联订单列表项
 */
export interface IHnInvoiceApplicationOrderItem {
  /** 主键 */
  id: number;
  /** 关联的申请单ID */
  applicationId: number;
  /** 子订单编号 */
  orderDetailNo: string;
  /** 任务名称 */
  taskName: string;
  /** 收款人 */
  recipient: string;
  /** 身份证号 */
  idCardNumber: string;
  /** 手机号 */
  phoneNumber: string;
  /** 银行卡号 */
  bankCardNumber: string;
  /** 个人到账金额 */
  personalAmount: number;
  /** 订单创建时间 */
  orderCreateTime: string;
  /** 订单完成时间 */
  orderCompleteTime: string;
  /** 企业合同链接 */
  contractUrl: string;
  /** 回单链接 */
  receiptUrl: string;
  /** 含税金额 */
  taxAmount: number;
  /** 订单开票科目 */
  invoiceSubName: string;
  /** 本人授权有效期 */
  hnAuthRemainingDate: string;
}

/**
 * 查询完税发票申请单关联异常订单入参
 */
export interface IWarningOrdersParams extends IPageParam {
  /** 关联的申请单ID */
  applicationId?: number;
}

/**
 * 开票申请分页
 */
export interface IInvoiceApplyPageParams extends IPageParam {
  /** 完税申请单号 */
  code?: string;
  /** 审核备注 */
  approveMessage?: string;
  /** 企业ID */
  businessNo?: string;
  /** 处理状态(0未处理 7正在计算税款 1已税款计算 2已扣款成功 3扣款失败 4全部开票成功 5部分开票成功 6全部开票失败) */
  status?: EInvoiceApplyStatus;
  /** 发票上传状态 */
  offlineInvoiceStatus?: EInvoiceUploadStatus;
  /** 完税凭证上传状态 */
  offlineTaxStatus?: EInvoiceUploadStatus;
  /** 所属特代 */
  topAgentName?: string;
  /** 企业名称 */
  companyName?: string;
  /** 业务主体类型(2企业 3服务商) */
  businessType?: 2 | 3;
  /** 税金支付时间(起) */
  payTimeStart?: string;
  /** 税金支付时间(止) */
  payTimeEnd?: string;
  /** 发票科目 */
  invoiceSubName?: string;
  /** 发票开具时间(起) */
  invoiceTimeStart?: string;
  /** 发票开具时间(止) */
  invoiceTimeEnd?: string;
  /** 服务商名称 */
  serverName?: string;
  /** 直属代理商 */
  agentName?: string; 
  /** 海南开票方式 */
  hnInvoiceType?: EHnInvoiceType;
  /** 申请时间(起) */
  createTimeStart?: string;
  /** 申请时间(止) */
  createTimeEnd?: string;
}

/**
 * 批量下载发票、完税证明入参
*/
export interface IExportTaxInvoicesParams extends IInvoiceApplyPageParams {
  /** 业务主体ID */
  businessId: number;
  /** 业务主体类型(2企业 3服务商) */
  businessType: 2 | 3;
}

/**
 * 海南开票申请列表项
 */
export interface IHnInvoiceApplyItem {
  /** 开票申请ID */
  id: number;
  /** 业务主体ID */
  businessId: number;
  /** 业务主体类型(2企业 3服务商) */
  businessType: number;
  /** 业务单号 */
  businessNo: string;
  /** 开票总金额 */
  invoiceAmount: number;
  /** 企业名称 */
  companyName: string;
  /** 所属特代 */
  topAgentName: string;
  /**
   * 处理状态
   * 0: 未处理
   * 7: 正在计算税款
   * 1: 已税款计算
   * 2: 已扣款成功
   * 3: 扣款失败
   * 4: 全部开票成功
   * 5: 部分开票成功
   * 6: 全部开票失败
   */
  status: EInvoiceApplyStatus;
  /** 发票上传状态 */
  offlineInvoiceStatus?: EInvoiceUploadStatus;
  /** 完税凭证上传状态 */
  offlineTaxStatus?: EInvoiceUploadStatus;
  /** 汇总发票url */
  invoiceFileUrl: string;
  /** 申请成功订单数 */
  successCount: number;
  /** 申请失败订单数 */
  errorCount: number;
  /** 税金支付时间 */
  payTime: string;
  /** 税金 */
  taxAmount: number;
  /** 企业支付税金 */
  companyTaxAmount: number;
  /** 完税发票科目 */
  invoiceSubName: string;
  /** 完税发票科目No */
  code: string;
  /** 服务商名称 */
  serverName: string;
  /** 发票开具时间 */
  invoiceTime: string;
  /** 驳回原因 */
  approveMessage: string;
  /** 申请时间 */
  createTime?: string;

}

export enum EInvoiceApplyStatus {
  /** 税务审核 */
  TaxAudit = 0,
  /** 待支付 */
  PendingPayment = 1,
  /** 待开票 */
  PendingInvoice = 2,
  /** 已开票 */
  Invoiced = 3,
  /** 内部审核 */
  InternalAudit = 4,
  /** 开票失败 */
  InvoiceFailed = 5,
  /** 审核驳回 */
  Rejected = 6,
  /** 审核通过 */
  Pass = 7,
}

export const invoiceApplyStatusLabel = {
  [EInvoiceApplyStatus.TaxAudit]: '税务审核',
  [EInvoiceApplyStatus.PendingPayment]: '待支付',
  [EInvoiceApplyStatus.PendingInvoice]: '待开票',
  [EInvoiceApplyStatus.Invoiced]: '已开票',
  [EInvoiceApplyStatus.InternalAudit]: '内部审核',
  [EInvoiceApplyStatus.InvoiceFailed]: '开票失败',
  [EInvoiceApplyStatus.Rejected]: '审核驳回',
  [EInvoiceApplyStatus.Pass]: '审核通过',
};

export const invoiceApplyStatusStyle = {
  [EInvoiceApplyStatus.TaxAudit]: 'primary',
  [EInvoiceApplyStatus.PendingPayment]: 'primary',
  [EInvoiceApplyStatus.PendingInvoice]: 'primary',
  [EInvoiceApplyStatus.Invoiced]: 'success',
  [EInvoiceApplyStatus.InternalAudit]: 'primary',
  [EInvoiceApplyStatus.InvoiceFailed]: 'danger',
  [EInvoiceApplyStatus.Rejected]: 'danger',
  [EInvoiceApplyStatus.Pass]: 'success',
};

export enum EInvoiceUploadStatus {
  /** 未上传 */
  No = 0,
  /** 已上传 */
  Yes = 1,
}

export const invoiceUploadStatusLabel = {
  [EInvoiceUploadStatus.No]: '未上传',
  [EInvoiceUploadStatus.Yes]: '已上传',
};

export const invoiceUploadStatusStyle = {
  [EInvoiceUploadStatus.No]: 'danger',
  [EInvoiceUploadStatus.Yes]: 'success',
};

/**
 * 获取个税支付H5地址入参
 */
export interface IInvoicePayH5UrlParams {
  /** 发票申请单ID */
  id: number;
  /** 支付方式 */
  payMode: EHnInvoiceApplyPay;
}

/**
 * 获取个税支付H5地址响应数据
 */
export interface IInvoicePayH5Url {
  /** 订单状态（0:待支付） */
  orderStatus: number;
  /** 支付跳转链接 */
  counterUrl: string;
}

/**
 * 开票申请详情入参
 */
export interface IHnInvoiceDetailParams extends IPageParam {
  /** 开票申请ID */
  applyId: number;
  /** 收款人 */
  name?: string;
  /** 身份证号 */
  idNo?: string;
  /** 企业Id */
  companyId?: string;
}

/**
 * 海南开票详情列表项
 */
export interface IHnInvoiceDetailItem {
  /** 开票子订单ID */
  id: number;
  /** 子订单ID */
  orderDetailId: number;
  /** 子订单编号 */
  orderDetailNo: string;
  /** 任务名称 */
  dutyName: string;
  /** 身份证号码 */
  idNo: string;
  /** 姓名 */
  name: string;
  /** 手机号码 */
  phone: string;
  /** 银行卡号 */
  bankCardNumber: string;
  /** 开票金额 */
  invoiceAmount: number;
  /** 状态(0通过 1驳回) */
  status: 0 | 1;
  /** 驳回原因 */
  errorMessage: string;
  /** 服务商Id */
  serverId: number;
  /** 回单链接 */
  receiptUrl: string;
  /** 本人授权有效期 */
  hnAuthRemainingDate: string;
  /** 合同链接 */
  contractUrl: string;
}

/**
 * 申请记录入参
 */
export interface IInvoiceApplyRecordParams extends IPageParam {
  /** 审批单号码 */
  approvalNo?: string;
  /** 企业名称 */
  companyName?: string;
  /** 服务商名称 */
  superagentName?: string;
  /** 申请状态 	审批状态\n REVIEWING：审核中\n APPROVED：通过\n REJECTED：驳回\n */
  approvalStatus?: EInvoiceApplyRecordStatus;
  /** 开票科目 */
  invoiceSubjectId?: number;
  /** 业务主体类型\n SERVER：服务商\n COMPANY：企业\n */
  businessType?: 'SERVER' | 'COMPANY';
}

export enum EInvoiceApplyRecordStatus {
  /** 审核中 */
  REVIEWING = 'REVIEWING',
  /** 通过 */
  APPROVED = 'APPROVED',
  /** 驳回 */
  REJECTED = 'REJECTED',
}

export const invoiceApplyRecordStatusLabel = {
  [EInvoiceApplyRecordStatus.REVIEWING]: '审核中',
  [EInvoiceApplyRecordStatus.APPROVED]: '通过',
  [EInvoiceApplyRecordStatus.REJECTED]: '驳回',
};

export const invoiceApplyRecordStatusStyle = {
  [EInvoiceApplyRecordStatus.REVIEWING]: 'primary',
  [EInvoiceApplyRecordStatus.APPROVED]: 'success',
  [EInvoiceApplyRecordStatus.REJECTED]: 'danger',
};

/**
 * 申请记录
 */
export interface IInvoiceApplyRecordItem {
  /** 主键 */
  id: number;
  /** 审批单号码 */
  approvalNo: string;
  /** 开票科目 */
  invoiceSubjectId: number;
  /** 开票科目名称 */
  invoiceSubjectName: string;
  /** 合并订单数 */
  orderCount: number;
  /** 合并订单金额 */
  orderAmount: number;
  /** 发票金额 */
  invoiceAmount: number;
  /** 提交时间 */
  submissionTime: string;
  /** 审批日期 */
  approvalTime: string;
  /**
   * 审批状态
   * REVIEWING：审核中
   * APPROVED：通过
   * REJECTED：驳回
   */
  approvalStatus: EInvoiceApplyRecordStatus;
  /** 审批备注 */
  approvalNotes: string;
  /** 企业ID */
  companyId: number;
  /** 企业名称 */
  companyName: string;
  /** 特级代理商ID */
  superagentId: number;
  /** 特级代理商名称 */
  superagentName: string;
  /** 海南开票方式 */
  invoiceApplyChannel: EHnInvoiceType;
}

/**
 * 申请记录科目列表
 */
export interface IRecordSubjectListParams {
  /** 业务主体ID */
  businessId: number;
  /** 业务主体类型\n SERVER：服务商\n COMPANY：企业\n */
  businessType?: 'SERVER' | 'COMPANY';
}

/**
 * 月度开票统计响应
 */
export interface IMonthInvoiceStatistics {
  /** 可开票总数 */
  invoiceTotal: number;
  /** 已开票数 */
  invoiceNum: number;
  /** 申请开票数 */
  invoiceApplyNum: number;
  /** 剩余开票数 */
  invoiceCountRemaining: number;
}

/**
 * 月度开票统计分页入参
 */
export interface IMonthInvoiceParams extends IPageParam {
  /** 企业ID/服务商ID */
  businessId?: number;
  /** 业务主体类型(2企业 3服务商) */
  businessType?: 2 | 3;
}

/**
 * 月度开票分页项
 */
export interface IMonthInvoiceItem {
  /** 可开票总数 */
  invoiceTotal?: number;
  /** 已开票数 */
  invoiceNum?: number;
  /** 申请开票数 */
  invoiceApplyNum?: number;
  /** 剩余开票数 */
  invoiceCountRemaining?: number;
  /** 业务主体ID */
  businessId?: number;
  /** 业务主体类型(2企业 3服务商) */
  businessType?: 2 | 3;
  /** 企业名称 */
  companyName?: string;
  /** 配置ID */
  id?: number;
  configId?: number;
  /** 月开票张数限制 */
  invoiceCount?: number;
  /** 月开票金额 */
  invoiceMonth?: number;
}

/**
 * 月度开票统计 - 企业/服务商已入驻集合查询
 */
export interface IMonthCompanyItem {
  /** 业务主体ID */
  businessId?: number;
  /** 企业名称 */
  companyName?: string;
  /** 统一社会信用代码 */
  creditCode?: string;
  /** 公司编号 */
  code?: string;
}

/**
 * 众包 - 开票申请入参
 */
export interface ICrowdInvoiceApplyParams extends IPageParam {
  /** 科目ID */
  invoiceSubId?: number;
  /** 服务商ID */
  serverId?: number;
  /** 企业ID */
  companyId?: number;
  /** 子订单编号 */
  orderDetailNo?: string;
  /** 自由职业者 */
  freelancer?: string;
  /** 任务ID */
  dutyId?: number;
  /** 任务编号 */
  dutyNo?: string;
  /** 订单创建时间（起） */
  orderStartTimeBegin?: string;
  /** 订单创建时间（止） */
  orderStartTimeEnd?: string;
  /** 订单完成时间（起） */
  orderEndTimeBegin?: string;
  /** 订单完成时间（止） */
  orderEndTimeEnd?: string;
  /** 在授权有效期内 */
  inHnAuthTime?: boolean;
  /** 合同签署状态 */
  contractSignStatus?: boolean;
}

/**
 * 众包 - 开票申请返参
 */
export interface ICrowdInvoiceApplyItem {
  /** 科目ID */
  invoiceSubId?: number;
  /** 科目名称 */
  invoiceSubName?: string;
  /** 子订单ID */
  orderDetailId?: number;
  /** 子订单编号 */
  orderDetailNo?: string;
  /** 任务ID */
  dutyId?: number;
  /** 任务编号 */
  dutyNo?: string;
  /** 任务名称 */
  dutyName?: string;
  /** 含税金额 */
  taxAmount?: number;
  /** 订单创建时间（起） */
  orderStartTime?: string;
  /** 订单创建时间（止） */
  orderEndTime?: string;
  /** 收款人姓名 */
  personName?: string;
  /** 收款人身份证号 */
  personIdNumber?: string;
  /** 收款人手机号 */
  personTel?: string;
  /** 收款人银行卡号 */
  personBank?: string;
  /** 收款人到账金额 */
  receivedAmount?: number;
  /** 是否在授权有效期内 */
  inHnAuthTime?: boolean;
  /** 海南授权剩余时间 */
  hnAuthRemainingDate?: string;
  /** 合同签署状态 */
  contractSignStatus?: boolean;
  /** 订单是否被打回 */
  rejected?: boolean;
  /** 订单是否超额 */
  isExceed?: boolean;
  /** 海南企业绑定状态（0：未绑定 1：已绑定） */
  hnBindStatus?: 0 | 1;
}

/**
 * 众包 - 开票申请 - 开票科目、任务列表
 */
export interface ICrowdInvoiceSubjectAndDutyList {
  /** 发票科目列表 */
  invoiceSubjects?: ICrowdInvoiceSubjectItem[];
  /** 任务列表 */
  duties?: ICrowdInvoiceSubjectItem[];
}

/**
 * 众包 - 开票申请 - 开票科目列表项
 */
export interface ICrowdInvoiceSubjectItem {
  /** 科目ID */
  invoiceSubId?: number;
  /** 科目名称 */
  invoiceSubName?: string;
  /** 任务ID */
  dutyId?: number;
  /** 任务名称 */
  dutyName?: string;
}

/**
 * 众包 - 开票申请 - 计算子订单汇总免税-含税金额
 */
export interface IComputeFreeTaxOrderAmount {
  /** 科目名。多条以顿号分割 */
  invoiceSubNames?: string[];
  /** 小额免税订单金额 */
  freeTaxOrderAmount?: number;
  /** 标准含税订单金额 */
  standardOrderAmount?: number;
}

/**
 * 撮合 - 开票申请 - 计算子订单汇总免税-含税金额 请求参数
 */
export interface IMatchTaxOrderAmountReq {
  /** 企业ID */
  companyId: number;
  /** 发票科目ID */
  invoiceSubId: number;
  /** 子订单ID列表 */
  orderDetailIds: number[];
}

/**
 * 众包 - 服务商列表
 */
export interface ICrowdServerItem extends IServerOptionsItem {
  /** 存在待申请 0否 1是 */
  havePendingApply?: 0 | 1;
  /** 海南开票方式 */
  hnInvoiceType?: EHnInvoiceType;
}

/**
 * 撮合 - 企业列表
 */
export interface ICrowdCompanyItem extends IEnterpriseItem {
  /** 存在待申请 0否 1是 */
  havePendingApply?: 0 | 1;
}

/**
 * 线下完税 - 上传完税申请文件（发票-完税凭证）
 */
export interface IUploadApplyFile {
  /** 完税开票申请ID */
  applyId?: number;
  /** 文件业务类型(0发票 1完税凭证) */
  fileBizType?: 0 | 1;
  /** 文件url集合 */
  files?: string[];
}

export interface ICheckInoviceRes {
  /* 笔数 */
  orderExceedCount: number;
  /* 金额 */
  distributeExceedAmount: number;
}